import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { announce } from "@react-aria/live-announcer";
import classNames from "classnames";
import { clamp } from "lodash";
import { useTranslation } from "react-i18next";
import { ZoomButton } from "./ZoomButton";
import type { RangeSliderProps, RangeSliderValue } from "./types";
import { buildTickLabels, generateTicks, getKeyboardStep, valueToPercent } from "./utils";
import styles from "./RangeSlider.module.scss";

/**
 * When the selected range exceeds this threshold, pipe labels are hidden
 * on small containers (mobile) via the `tickRowDense` CSS class.
 */
const DENSE_LABEL_THRESHOLD = 100;

/** Maps keyboard keys to their delta direction and multiplier type. */
const KEY_DELTA_MAP: Record<string, { direction: 1 | -1; useStep: boolean }> = {
    ArrowRight: { direction: 1, useStep: false },
    ArrowUp: { direction: 1, useStep: false },
    ArrowLeft: { direction: -1, useStep: false },
    ArrowDown: { direction: -1, useStep: false },
    PageUp: { direction: 1, useStep: true },
    PageDown: { direction: -1, useStep: true },
};

/**
 * Determines the viewport (visible range on the track) from a committed value.
 * - Full range: viewport spans min–max (no zoom).
 * - Partial selection: viewport matches the selection exactly, knobs sit at edges.
 */
function deriveViewport(value: RangeSliderValue, min: number, max: number) {
    const isFullRange = value.start === min && value.end === max;
    return isFullRange ? { start: min, end: max } : { start: value.start, end: value.end };
}

export function RangeSlider({ min, max, value, onChange, minGap = 0, labels }: RangeSliderProps) {
    const { t } = useTranslation();
    const {
        ariaLabel,
        startLabel,
        endLabel,
        expandStartLabel,
        expandEndLabel,
        startKnobValueTextKey,
        endKnobValueTextKey,
        rangeSummaryKey,
    } = labels;

    // Current knob positions — updates on every pointermove during drag
    const [current, setCurrent] = useState<RangeSliderValue>(value);

    // Viewport defines the visible range on the track.
    // Only updates on commit (never during drag) to keep the track stable.
    const [viewport, setViewport] = useState(() => deriveViewport(value, min, max));

    // --- Refs ---
    const startRef = useRef(current.start);
    const endRef = useRef(current.end);
    const draggingRef = useRef<"start" | "end" | null>(null);
    const rafRef = useRef<number | null>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const eventsCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (eventsCleanupRef.current) eventsCleanupRef.current();
        };
    }, []);

    // Stores the last value passed to onChange so the external-sync effect can
    // distinguish a parent "echo" (same value we just committed) from a genuine
    // external change (e.g. reset). Cleared after one skip cycle.
    const lastCommittedRef = useRef<RangeSliderValue | null>(null);

    // Guards against handleTrackClick firing from a bubbled pointerup after drag.
    // Releasing the knob triggers both onUp (correct commit) and a click on .track.
    const dragEndedRef = useRef(false);

    // --- External prop sync ---
    // Only applies when not dragging. Skips when the incoming value matches
    // the last committed value (parent echoing back). Always syncs when the
    // value differs (e.g. external reset).
    useEffect(() => {
        if (draggingRef.current) return;

        const isParentReflectingOurLastChange =
            lastCommittedRef.current != null &&
            value.start === lastCommittedRef.current.start &&
            value.end === lastCommittedRef.current.end;

        // Always clear after comparison — one skip per commit at most
        lastCommittedRef.current = null;

        if (isParentReflectingOurLastChange) return;

        const isOutOfBounds = value.start < min || value.start > max || value.end < min || value.end > max;
        if (isOutOfBounds) return;

        const isBelowMinGap = value.end - value.start < minGap;
        if (isBelowMinGap) return;

        setCurrent(value);
        startRef.current = value.start;
        endRef.current = value.end;
        setViewport(deriveViewport(value, min, max));
    }, [value, min, max, minGap]);

    // --- Derived values ---
    const isZoomed = viewport.start !== min || viewport.end !== max;
    const selectedRange = current.end - current.start;
    const isStartAtMin = current.start === min;
    const isEndAtMax = current.end === max;

    // Knob positions as percentages within the current viewport
    const startPercent = valueToPercent(current.start, viewport.start, viewport.end);
    const endPercent = valueToPercent(current.end, viewport.start, viewport.end);

    // Tick dots and labels are derived from the viewport (stable during drag)
    const ticks = useMemo(() => generateTicks(viewport.start, viewport.end), [viewport]);
    const tickLabels = useMemo(() => buildTickLabels(ticks, viewport.start, viewport.end), [ticks, viewport]);

    // --- Track metrics ---
    const getTrackMetrics = useCallback(() => {
        const el = trackRef.current;
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return { left: rect.left, width: rect.width };
    }, []);

    // --- Commit ---
    // Finalises the value, updates viewport, and notifies parent.
    // Gap enforcement is done by each handler before calling commit.
    const commit = useCallback(
        (next: RangeSliderValue) => {
            const normalized: RangeSliderValue = {
                start: clamp(next.start, min, max),
                end: clamp(next.end, min, max),
            };

            lastCommittedRef.current = normalized;

            startRef.current = normalized.start;
            endRef.current = normalized.end;
            setCurrent(normalized);
            setViewport(deriveViewport(normalized, min, max));
            onChange(normalized);
        },
        [min, max, onChange],
    );

    // --- Pointer drag ---
    const handlePointerDown = useCallback(
        (knob: "start" | "end") => (e: React.PointerEvent) => {
            e.preventDefault();
            draggingRef.current = knob;

            const knobEl = e.currentTarget as HTMLElement;
            knobEl.setPointerCapture(e.pointerId);

            const metrics = getTrackMetrics();
            if (!metrics) return;

            // Capture viewport at drag start so it stays stable for the entire gesture
            const vpStart = viewport.start;
            const vpEnd = viewport.end;

            const onMove = (ev: PointerEvent) => {
                if (rafRef.current) return;
                rafRef.current = requestAnimationFrame(() => {
                    rafRef.current = null;

                    const percent = (ev.clientX - metrics.left) / metrics.width;
                    const val = Math.round(vpStart + clamp(percent, 0, 1) * (vpEnd - vpStart));

                    if (draggingRef.current === "start") {
                        const next = clamp(val, min, endRef.current - minGap);
                        startRef.current = next;
                        setCurrent((prev) => ({ ...prev, start: next }));
                    } else {
                        const next = clamp(val, startRef.current + minGap, max);
                        endRef.current = next;
                        setCurrent((prev) => ({ ...prev, end: next }));
                    }
                });
            };

            const onUp = () => {
                draggingRef.current = null;
                dragEndedRef.current = true;
                eventsCleanupRef.current = null;
                knobEl.removeEventListener("pointermove", onMove);
                knobEl.removeEventListener("pointerup", onUp);
                if (rafRef.current) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }
                commit({ start: startRef.current, end: endRef.current });
            };

            knobEl.addEventListener("pointermove", onMove);
            knobEl.addEventListener("pointerup", onUp, { once: true });

            eventsCleanupRef.current = () => {
                knobEl.removeEventListener("pointermove", onMove);
                knobEl.removeEventListener("pointerup", onUp);
            };
        },
        [viewport, min, max, minGap, getTrackMetrics, commit],
    );

    // --- Track click ---
    // Moves the nearest knob to the clicked position within the viewport.
    const handleTrackClick = useCallback(
        (e: React.MouseEvent) => {
            // Ignore the click that bubbles from a knob's pointerup
            if (dragEndedRef.current) {
                dragEndedRef.current = false;
                return;
            }
            if (draggingRef.current) return;

            const metrics = getTrackMetrics();
            if (!metrics) return;

            // Map pixel position to value within the current viewport
            const percent = (e.clientX - metrics.left) / metrics.width;
            const val = Math.round(viewport.start + clamp(percent, 0, 1) * (viewport.end - viewport.start));

            // Move whichever knob is closer to the click
            const distStart = Math.abs(val - current.start);
            const distEnd = Math.abs(val - current.end);

            if (distStart <= distEnd) {
                commit({
                    start: clamp(val, min, current.end - minGap),
                    end: current.end,
                });
            } else {
                commit({
                    start: current.start,
                    end: clamp(val, current.start + minGap, max),
                });
            }
        },
        [current, viewport, min, max, minGap, getTrackMetrics, commit],
    );

    // --- Keyboard navigation ---
    const handleKeyDown = useCallback(
        (knob: "start" | "end") => (e: React.KeyboardEvent) => {
            const step = getKeyboardStep(viewport.end - viewport.start);

            // Home/End are special — they jump to boundaries
            if (e.key === "Home") {
                e.preventDefault();
                const next =
                    knob === "start"
                        ? { start: min, end: current.end }
                        : { start: current.start, end: current.start + minGap };
                commit(next);
                return;
            }
            if (e.key === "End") {
                e.preventDefault();
                const next =
                    knob === "start"
                        ? { start: current.end - minGap, end: current.end }
                        : { start: current.start, end: max };
                commit(next);
                return;
            }

            // Arrow and Page keys use a delta lookup
            const keyConfig = KEY_DELTA_MAP[e.key];
            if (!keyConfig) return;

            e.preventDefault();
            const delta = keyConfig.direction * (keyConfig.useStep ? step : 1);

            const next: RangeSliderValue =
                knob === "start"
                    ? { start: clamp(current.start + delta, min, current.end - minGap), end: current.end }
                    : { start: current.start, end: clamp(current.end + delta, current.start + minGap, max) };

            commit(next);
        },
        [current, viewport, min, max, minGap, commit],
    );

    // --- Zoom button handlers ---
    const handleExpandStart = useCallback(() => {
        commit({ start: min, end: current.end });
        announce(t("rangeSlider.rangeStartSetTo", { value: min }), "polite");
    }, [min, current.end, commit, t]);
    const handleExpandEnd = useCallback(() => {
        commit({ start: current.start, end: max });
        announce(t("rangeSlider.rangeEndSetTo", { value: max }), "polite");
    }, [max, current.start, commit, t]);

    // --- Render ---
    return (
        <div className={styles.root} role="group" aria-label={ariaLabel}>
            {/* Tick labels row */}
            <div
                className={classNames(styles.tickRow, selectedRange > DENSE_LABEL_THRESHOLD && styles.tickRowDense)}
                aria-hidden="true"
            >
                {tickLabels.map((tickLabel, index) => (
                    <span
                        key={`${tickLabel.type}-${tickLabel.value}-${index}`}
                        className={classNames(
                            styles.tickItem,
                            tickLabel.type === "value" ? styles.tickItemValue : styles.tickItemPipe,
                        )}
                        style={{ left: `${tickLabel.percent}%` }}
                    >
                        {tickLabel.type === "value" ? tickLabel.value : "|"}
                    </span>
                ))}
            </div>

            {/* Track row with desktop zoom buttons */}
            <div className={styles.trackRow}>
                {isZoomed && (
                    <ZoomButton
                        direction="left"
                        label={expandStartLabel}
                        disabled={isStartAtMin}
                        onClick={handleExpandStart}
                        className={styles.zoomBtnDesktopStart}
                    />
                )}

                {/* Track click is a pointer-only convenience — keyboard users interact with the knobs directly */}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                <div className={styles.track} onClick={handleTrackClick}>
                    <div ref={trackRef} className={styles.trackInner}>
                        <div className={styles.trackLine} />

                        {/* Active range highlight */}
                        <div
                            className={styles.trackActive}
                            style={{
                                left: `${startPercent}%`,
                                width: `${endPercent - startPercent}%`,
                            }}
                        />

                        {/* Tick dots */}
                        {ticks.map((tick) => (
                            <div
                                key={tick.value}
                                className={classNames(
                                    styles.tickDot,
                                    tick.percent >= startPercent && tick.percent <= endPercent && styles.tickDotActive,
                                )}
                                style={{ left: `${tick.percent}%` }}
                            />
                        ))}

                        {/* Start knob */}
                        <div
                            className={styles.knob}
                            style={{ left: `${startPercent}%` }}
                            role="slider"
                            tabIndex={0}
                            aria-label={startLabel}
                            aria-valuemin={min}
                            aria-valuemax={max}
                            aria-valuenow={current.start}
                            aria-valuetext={t(startKnobValueTextKey, { value: current.start, count: selectedRange })}
                            onPointerDown={handlePointerDown("start")}
                            onKeyDown={handleKeyDown("start")}
                        >
                            <span className={styles.knobTooltip} aria-hidden="true">
                                {current.start}
                            </span>
                        </div>

                        {/* End knob */}
                        <div
                            className={styles.knob}
                            style={{ left: `${endPercent}%` }}
                            role="slider"
                            tabIndex={0}
                            aria-label={endLabel}
                            aria-valuemin={min}
                            aria-valuemax={max}
                            aria-valuenow={current.end}
                            aria-valuetext={t(endKnobValueTextKey, { value: current.end, count: selectedRange })}
                            onPointerDown={handlePointerDown("end")}
                            onKeyDown={handleKeyDown("end")}
                        >
                            <span className={styles.knobTooltip} aria-hidden="true">
                                {current.end}
                            </span>
                        </div>
                    </div>
                </div>

                {isZoomed && (
                    <ZoomButton
                        direction="right"
                        label={expandEndLabel}
                        disabled={isEndAtMax}
                        onClick={handleExpandEnd}
                        className={styles.zoomBtnDesktopEnd}
                    />
                )}
            </div>

            {/* Summary row with mobile zoom buttons */}
            <div className={styles.selectedRangeRow}>
                {isZoomed && (
                    <ZoomButton
                        direction="left"
                        label={expandStartLabel}
                        disabled={isStartAtMin}
                        onClick={handleExpandStart}
                        className={styles.zoomBtnMobileStart}
                    />
                )}
                <span className="textsml coloralt" aria-hidden="true">
                    {t(rangeSummaryKey, { count: selectedRange })}
                </span>
                {isZoomed && (
                    <ZoomButton
                        direction="right"
                        label={expandEndLabel}
                        disabled={isEndAtMax}
                        onClick={handleExpandEnd}
                        className={styles.zoomBtnMobileEnd}
                    />
                )}
            </div>
        </div>
    );
}
